import { useTranslation } from "react-i18next";
import { Languages, Check } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  SUPPORTED_LANGUAGES,
  AVAILABLE_LANGUAGE_CODES,
  type SupportedLanguageCode,
} from "@/i18n";

/**
 * MYID-D6a (2026-05-25) — lift of karePortal KP9-3 LanguagePicker.
 *
 * Lives in the top header. Lists the 5 supported languages (en/es/te
 * available; hi/ta marked Coming soon and disabled). Selecting a code
 * calls i18n.changeLanguage which:
 *   1. Updates the live tree (react-i18next re-renders consumers)
 *   2. Detector writes the new code to localStorage (key:
 *      myCollabkareId:locale, set by src/i18n/index.ts)
 *   3. Fires `languageChanged` → applyDocumentLocale syncs <html lang>
 *      and <html dir>
 *
 * Future follow-up (mirrors KP9-18): also write-through to KC's
 * `locale` user attribute so the choice survives device switches.
 * Today this is device-local only.
 */
export default function LanguagePicker() {
  const { i18n, t } = useTranslation("common");

  // MYID-G5 (2026-05-25) — defensive. i18n may not be fully initialized
  // on the first render (the init promise resolves asynchronously).
  // Without these guards, accessing i18n.resolvedLanguage / i18n.dir()
  // during the first paint can throw and silently get caught by React's
  // error boundary, leaving the picker invisible in the rendered DOM.
  // resolvedLanguage is optional in the i18next types; the fallback
  // chain is: resolvedLanguage → language → 'en'.
  const rawLang = i18n?.resolvedLanguage || i18n?.language || "en";
  const current = (
    AVAILABLE_LANGUAGE_CODES.includes(rawLang as SupportedLanguageCode)
      ? rawLang
      : "en"
  ) as SupportedLanguageCode;

  const handlePick = (code: SupportedLanguageCode) => {
    if (!i18n || !AVAILABLE_LANGUAGE_CODES.includes(code)) return;
    void i18n.changeLanguage(code);
  };

  const currentEntry =
    SUPPORTED_LANGUAGES.find((l) => l.code === current) ||
    SUPPORTED_LANGUAGES[0];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={t("language.picker.label", "Change language")}
          className="flex items-center gap-1.5 rounded-md px-2 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <Languages className="h-4 w-4" aria-hidden="true" />
          <span className="hidden sm:inline">{currentEntry.nativeLabel}</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <DropdownMenuLabel>
          {t("language.picker.heading", "Language")}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {SUPPORTED_LANGUAGES.map((lang) => {
          const isCurrent = lang.code === current;
          const isAvailable = lang.available;
          return (
            <DropdownMenuItem
              key={lang.code}
              disabled={!isAvailable}
              onSelect={(e: Event) => {
                if (!isAvailable) {
                  e.preventDefault();
                  return;
                }
                handlePick(lang.code as SupportedLanguageCode);
              }}
              className="flex items-center justify-between gap-2"
            >
              <span className="flex flex-col">
                <span className="text-sm">{lang.nativeLabel}</span>
                {!isAvailable && (
                  <span className="text-xs text-muted-foreground">
                    {t("language.comingSoon", "Coming soon")}
                  </span>
                )}
              </span>
              {isCurrent && <Check className="h-4 w-4" aria-hidden="true" />}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
